import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Popconfirm,
  message,
  InputNumber,
  Row,
  Col,
  Rate,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { knowledgeApi } from '../../services/knowledge';
import type { CooperationRecord, CreateCooperationRecordRequest } from '../../types/knowledge';
import type { PaginationParams } from '../../types/api';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const PLATFORMS = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
];
const CONTENT_TYPES = [
  { value: 'shorts', label: '短视频' },
  { value: 'video', label: '视频' },
  { value: '贴片', label: '贴片广告' },
  { value: '直播', label: '直播' },
];
const COOPERATION_TYPES = ['付费合作', '产品置换', '分成合作', '长期代言'];

const PLATFORM_COLOR_MAP: Record<string, string> = {
  youtube: 'red',
  tiktok: 'cyan',
  instagram: 'magenta',
};

export const CooperationRecords: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CooperationRecord[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filterPlatform, setFilterPlatform] = useState<string>('');
  const [filterCooperationType, setFilterCooperationType] = useState<string>('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<CooperationRecord | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [form] = Form.useForm();

  const fetchData = useCallback(async (params?: PaginationParams) => {
    setLoading(true);
    try {
      const response = await knowledgeApi.getCooperationRecords({
        page: params?.page || pagination.current,
        page_size: params?.page_size || pagination.pageSize,
      });
      // 后端返回格式: { data: T[], pagination: { total, page, page_size, total_pages } }
      setData(response.data || []);
      if (response.pagination) {
        setPagination({
          current: response.pagination.page,
          pageSize: response.pagination.page_size,
          total: response.pagination.total,
        });
      }
    } catch {
      // 错误已在request拦截器中处理
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleTableChange = (newPagination: { current?: number; pageSize?: number }) => {
    fetchData({
      page: newPagination.current,
      page_size: newPagination.pageSize,
    });
  };

  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: CooperationRecord) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      cooperation_date: record.cooperation_date?.split('T')[0],
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await knowledgeApi.deleteCooperationRecord(id);
      message.success('删除成功');
      setRefreshKey(prev => prev + 1);
    } catch (error: unknown) {
      console.error('[CooperationRecords] 操作失败:', error);
      message.error('操作失败，请检查权限或稍后重试');
    }
  };

  const handleSubmit = async (values: CreateCooperationRecordRequest) => {
    try {
      // 清理请求数据：移除空字符串或非UUID格式的可选字段，避免后端解析失败
      const cleanedValues = { ...values };
      const uuidFields = ['influencer_id', 'project_id', 'proposal_id', 'customer_id'];
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      uuidFields.forEach(field => {
        if (!cleanedValues[field as keyof typeof cleanedValues] || !uuidRegex.test(String(cleanedValues[field as keyof typeof cleanedValues]))) {
          delete cleanedValues[field as keyof typeof cleanedValues];
        }
      });
      if (cleanedValues.cooperation_date) {
        cleanedValues.cooperation_date = dayjs(cleanedValues.cooperation_date).toISOString();
      }
      if (editingRecord) {
        await knowledgeApi.updateCooperationRecord(editingRecord.id, cleanedValues);
        message.success('更新成功');
      } else {
        await knowledgeApi.createCooperationRecord(cleanedValues);
        message.success('创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setRefreshKey(prev => prev + 1);
    } catch (error: unknown) {
      console.error('[CooperationRecords] 操作失败:', error);
      message.error('操作失败，请检查权限或稍后重试');
    }
  };

  const filteredData = data.filter((item) => {
    const matchKeyword = !searchKeyword || 
      item.influencer_name.toLowerCase().includes(searchKeyword.toLowerCase());
    const matchPlatform = !filterPlatform || item.platform === filterPlatform;
    const matchCooperationType = !filterCooperationType || item.content_type === filterCooperationType;
    return matchKeyword && matchPlatform && matchCooperationType;
  });

  const columns: ColumnsType<CooperationRecord> = [
    {
      title: '红人名称',
      dataIndex: 'influencer_name',
      key: 'influencer_name',
      width: 120,
    },
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      width: 100,
      render: (platform: string) => (
        <Tag color={PLATFORM_COLOR_MAP[platform] || 'default'}>{platform}</Tag>
      ),
    },
    {
      title: '合作类型',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 100,
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: '费用',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (price: number) => `¥${price?.toLocaleString() || 0}`,
    },
    {
      title: 'CPM',
      dataIndex: 'cpm',
      key: 'cpm',
      width: 100,
      render: (cpm: number) => cpm ? `¥${cpm.toFixed(2)}` : '-',
    },
    {
      title: '合作评分',
      dataIndex: 'cooperation_score',
      key: 'cooperation_score',
      width: 150,
      render: (score: number) => (
        <Rate disabled value={score / 20} allowHalf style={{ fontSize: 14 }} />
      ),
    },
    {
      title: '内容质量',
      dataIndex: 'content_quality_score',
      key: 'content_quality_score',
      width: 150,
      render: (score: number) => (
        <Rate disabled value={score / 20} allowHalf style={{ fontSize: 14 }} />
      ),
    },
    {
      title: '合作日期',
      dataIndex: 'cooperation_date',
      key: 'cooperation_date',
      width: 120,
      render: (text: string) => text?.split('T')[0] || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: unknown, record: CooperationRecord) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此合作记录？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 搜索和操作栏 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space size="middle">
              <Input
                placeholder="搜索红人名称"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                prefix={<SearchOutlined />}
                style={{ width: 200 }}
                allowClear
              />
              <Select
                placeholder="平台筛选"
                value={filterPlatform || undefined}
                onChange={setFilterPlatform}
                style={{ width: 120 }}
                allowClear
              >
                {PLATFORMS.map((p) => (
                  <Option key={p.value} value={p.value}>{p.label}</Option>
                ))}
              </Select>
              <Select
                placeholder="合作类型"
                value={filterCooperationType || undefined}
                onChange={setFilterCooperationType}
                style={{ width: 120 }}
                allowClear
              >
                {CONTENT_TYPES.map((t) => (
                  <Option key={t.value} value={t.value}>{t.label}</Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              新建记录
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1300 }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
        }}
        onChange={handleTableChange}
      />

      {/* 新建/编辑弹窗 */}
      <Modal
        title={editingRecord ? '编辑合作记录' : '新建合作记录'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="influencer_name"
                label="红人名称"
                rules={[{ required: true, message: '请输入红人名称' }]}
              >
                <Input placeholder="请输入红人名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="influencer_id"
                label="红人ID"
              >
                <Input placeholder="可选，用于关联红人数据" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="platform"
                label="平台"
                rules={[{ required: true, message: '请选择平台' }]}
              >
                <Select placeholder="选择平台">
                  {PLATFORMS.map((p) => (
                    <Option key={p.value} value={p.value}>{p.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="content_type"
                label="合作类型"
                rules={[{ required: true, message: '请选择合作类型' }]}
              >
                <Select placeholder="选择合作类型">
                  {CONTENT_TYPES.map((t) => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="cooperation_date"
                label="合作日期"
                rules={[{ required: true, message: '请选择合作日期' }]}
              >
                <Input type="date" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="price"
                label="合作费用（元）"
                rules={[
                  { required: true, message: '请输入合作费用' },
                  { type: 'number', min: 0, message: '费用不能为负数' },
                ]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="cpm"
                label="CPM（元）"
                rules={[{ type: 'number', min: 0, message: 'CPM不能为负数' }]}
              >
                <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="conversion_rate" label="转化率（%）">
                <InputNumber min={0} max={100} precision={2} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cooperation_score"
                label="合作评分（0-100）"
                rules={[
                  { required: true, message: '请输入合作评分' },
                  { type: 'number', min: 0, max: 100, message: '评分范围为0-100' },
                ]}
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="0-100" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="content_quality_score"
                label="内容质量评分（0-100）"
                rules={[
                  { required: true, message: '请输入内容质量评分' },
                  { type: 'number', min: 0, max: 100, message: '评分范围为0-100' },
                ]}
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} placeholder="0-100" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="tags" label="标签">
            <Select mode="tags" placeholder="输入标签后按回车添加" />
          </Form.Item>
          <Form.Item name="notes" label="备注">
            <TextArea rows={3} placeholder="合作备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
